import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useScript } from '@hooks/useScript';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import './i18n/config';
import '@styles/GlobalStyles.css';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { SITE_URLS } from '@constants';
import { RoleSelector } from '@pages/RoleSelector';
import { MyProfile } from '@pages/MyProfile';
import { DataProvider } from 'src/context/dataContext';
import { NotificationsProvider } from 'src/context/notificationsContext';
import { EditMyProfile } from '@pages/EditMyProfile';
import { EditVehicle } from '@pages/EditVehicle';
import { RecoverPassword } from '@pages/RecoverPassword';
import { ValidateEmail } from '@pages/ValidateEmail/ValidateEmail';
import { Login } from '@pages/Login';
import { MapPage } from '@pages/MapPage';
import { MultiStepForm } from '@components/organism/MultiStepForm/MultiStepForm';
import { PersonalInfoStep } from '@components/molecules/PersonalInfoStep/PersonalInfoStep';
import { OTPCodeStep } from '@components/molecules/OTPCodeStep/OTPCodeStep';
import { PasswordStep } from '@components/molecules/PasswordStep/PasswordStep';
import { CarInfoStep } from '@components/molecules/CarInfoStep/CarInfoStep';
import { WelcomeDriver } from '@components/molecules/WelcomeDriver/WelcomeDriver';
import { SiteSetup } from '@pages/SiteSetup';
import { DriversMapSetup } from '@pages/DriversMapSetup/DriversMapSetup';
import { UserGeoLocation } from '@pages/UserGeoLocation/UserGeoLocation';
import { UserLocationStatus } from '@pages/UserLocationStatus/UserLocationStatus';
import { TermsAndConditionsPage } from '@pages/TermsAndConditions/TermsAndConditionsPage';
import { PrivacyPolicyPage } from '@pages/PrivacyPolicy/PrivacyPolicyPage';
import { NotFound } from '@pages/NotFound/NotFound';
import { MyVehicles } from '@pages/MyVehicles';
import { useData } from '@hooks/useData';
import { InstallPWAPrompt } from '@components/atoms/InstallPWAPrompt/InstallPWAPrompt';
import { OfflineScreen } from '@components/molecules/OfflineScreen';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { setupFetchInterceptor } from '@utils/fetchInterceptor';
import {DriverLayout} from '@components/layouts/DriverLayout';
import {RiderRegistrationLayout} from '@components/layouts/RiderRegistrationLayout';
import {DriverRegistrationLayout} from '@components/layouts/DriverRegistrationLayout';

function App() {
    useScript({ url: 'https://kit.fontawesome.com/18bb4f4911.js' });

    const { allSettings } = useData();
    const { isOnline } = useOnlineStatus();
    const isPwaModuleActive = allSettings?.data?.module_pwa?.active;

    useEffect(() => {
        if (isPwaModuleActive) {
            serviceWorkerRegistration.register();
            return;
        }

        serviceWorkerRegistration.unregister();
    }, [isPwaModuleActive]);

    const availableRoutes = () => (
        <>
            {/* Public / Auth */}
            <Route path={SITE_URLS.selectAccountType} element={<RoleSelector />} />
            <Route path={SITE_URLS.login} element={<Login />} />
            <Route path={SITE_URLS.recoverPassword} element={<RecoverPassword />} />
            <Route path={SITE_URLS.validateRecoveryToken} element={<RecoverPassword />} />
            <Route path={SITE_URLS.emailVerification} element={<ValidateEmail/>}/>

            {/* Rider multi-step */}
            <Route element={<RiderRegistrationLayout />}>
                <Route path={SITE_URLS.createAccount} element={<MultiStepForm variant="rider" />}>
                    <Route index element={<Navigate to="personal" replace />} />
                    <Route path={SITE_URLS.personal} element={<PersonalInfoStep />} />
                    <Route path={SITE_URLS.otp} element={<OTPCodeStep />} />
                    <Route path={SITE_URLS.password} element={<PasswordStep />} />
                </Route>
            </Route>

            {/* Driver multi-step */}
            <Route element={<DriverRegistrationLayout />}>
                <Route path={SITE_URLS.createDriverAccount} element={<MultiStepForm variant="driver" />}>
                    <Route index element={<Navigate to="personal" replace />} />
                    <Route path={SITE_URLS.personal} element={<PersonalInfoStep />} />
                    <Route path={SITE_URLS.otp} element={<OTPCodeStep />} />
                    <Route path={SITE_URLS.car} element={<CarInfoStep />} />
                    <Route path={SITE_URLS.password} element={<PasswordStep />} />
                    <Route path={SITE_URLS.welcome} element={<WelcomeDriver />} />
                </Route>
            </Route>

            {/* Driver only */}
            <Route element={<DriverLayout />}>
                <Route path={SITE_URLS.editVehicle} element={<EditVehicle />} />
                <Route path={SITE_URLS.createVehicle} element={<EditVehicle />} />
                <Route path={SITE_URLS.myVehicles} element={<MyVehicles />} />
            </Route>

            {/* Shared */}
            <Route path={SITE_URLS.myProfile} element={<MyProfile />} />
            <Route path={SITE_URLS.editProfile} element={<EditMyProfile />} />
            <Route path={SITE_URLS.map} element={<MapPage />} />

            {/* Terms/policies */}
            <Route path={SITE_URLS.termsConditions} element={<TermsAndConditionsPage />} />
            <Route path={SITE_URLS.privacyPolicy} element={<PrivacyPolicyPage />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
            <Route path="/unauthorized" element={<NotFound />} />
        </>
    );

    if (!isOnline) {
        return <OfflineScreen />;
    }

    return (
        <>
            <InstallPWAPrompt />
            <Routes>
                <Route element={<SiteSetup />}>
                    <Route element={<DriversMapSetup />}>
                        <Route element={<UserGeoLocation />}>
                            <Route element={<UserLocationStatus />}>
                                <Route path="/" element={<Navigate to={SITE_URLS.login} replace />} />
                                {availableRoutes()}
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Routes>
        </>
    );
}

setupFetchInterceptor();
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <BrowserRouter>
        <React.StrictMode>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
                <DataProvider>
                    <NotificationsProvider>
                        <App />
                    </NotificationsProvider>
                </DataProvider>
            </GoogleOAuthProvider>
        </React.StrictMode>
    </BrowserRouter>
);

reportWebVitals();
